import { FunctionComponent } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import { Link as RouterLink } from 'react-resource-router';
import { ROUTES } from '@app/navigation';
import { CharlieTable, TableState } from '@atlassian/charlie-table';
import type { ColumnDef } from '@atlassian/charlie-table/tanstack-helpers';
import { Box, Stack, Text, xcss } from '@atlaskit/primitives';
import EmptyState from '@atlaskit/empty-state';
import Pagination from '@atlassian/pagination';
import { OptionType } from '@atlaskit/select';
import { useAppUpdatesContext } from '@app/app/src/AppContext';
import type { AppUpdatesContextStateType } from '@app/app/src/types';
import { useOneTrustContext } from '@app/onetrust';
import { formatBundleDate } from '@app/utils';
import { ProductList } from './ProductList';
import { UpdateChangeType } from './UpdateChangeType';
import { UnreadDotCell } from './UnreadDotCell';
import { useBundlePagination } from '../hooks/useBundlePagination';

const messages = defineMessages({
  emptyStateHeader: {
    id: 'bundledChangesTable.emptyState.header',
    defaultMessage: 'No bundled changes found',
    description: 'Header text for empty bundled changes table state',
  },
  emptyStateDescription: {
    id: 'bundledChangesTable.emptyState.description',
    defaultMessage:
      'No bundled changes match your filters. Adjust or clear your filters and try again.',
    description: 'Description text for empty bundled changes table state',
  },
  errorStateHeader: {
    id: 'bundledChangesTable.errorState.header',
    defaultMessage: 'Unable to load bundled changes',
    description: 'Header text for error bundled changes table state',
  },
  errorStateDescription: {
    id: 'bundledChangesTable.errorState.description',
    defaultMessage:
      "We couldn't load bundled changes. Refresh the page to try again.",
    description: 'Description text for error bundled changes table state',
  },
  productHeader: {
    id: 'bundledChangesTable.column.product',
    defaultMessage: 'App',
    description: 'Column header for product/app name',
  },
  releaseNoteHeader: {
    id: 'bundledChangesTable.column.releaseNote',
    defaultMessage: 'Release note',
    description: 'Column header for release note',
  },
  changeTypeHeader: {
    id: 'bundledChangesTable.column.changeType',
    defaultMessage: 'Change type',
    description: 'Column header for change type',
  },
  previewTrackHeader: {
    id: 'bundledChangesTable.column.previewTrack',
    defaultMessage: 'Preview release date',
    description: 'Column header for preview track release date',
  },
  bundledTrackHeader: {
    id: 'bundledChangesTable.column.bundledTrack',
    defaultMessage: 'Bundled release date',
    description: 'Column header for bundled track release date',
  },
});

const titleStyles = xcss({
  whiteSpace: 'normal',
});

export const BundledChangesTable: FunctionComponent = () => {
  const { formatMessage } = useIntl();
  const { state } = useAppUpdatesContext();
  const { analyticsOptOut } = useOneTrustContext();
  const { setCursor, setLimit } = useBundlePagination();

  const onNextPageClicked = () => {
    if (state.bundles.links.next) {
      setCursor(state.bundles.links.next);
    }
  };
  const onPreviousPageClicked = () => {
    if (state.bundles.links.prev) {
      setCursor(state.bundles.links.prev);
    }
  };
  const onPageSizeChange = (value: OptionType) => {
    setLimit(value.value as number);
  };

  const unreadIndicatorColumn: ColumnDef<
    AppUpdatesContextStateType['bundles']['data'][0]
  > = {
    id: 'unreadDot',
    header: '',
    accessorKey: 'id',
    size: 24,
    enableSorting: false,
    cell: ({ row }) => {
      if (analyticsOptOut || !row.original.firstPublishedAt) {
        return null;
      }
      return (
        <UnreadDotCell
          id={row.original.id}
          firstPublishedAt={row.original.firstPublishedAt}
        />
      );
    },
  };

  const columnDefs: ColumnDef<
    AppUpdatesContextStateType['bundles']['data'][0]
  >[] = [
    unreadIndicatorColumn,
    {
      id: 'product',
      header: formatMessage(messages.productHeader),
      accessorKey: 'productTypes',
      size: 150,
      enableSorting: false,
      cell: ({ row }) => (
        <ProductList productTypes={row.original.productTypes} />
      ),
    },
    {
      id: 'title',
      header: formatMessage(messages.releaseNoteHeader),
      accessorKey: 'title',
      size: 400,
      enableSorting: false,
      cell: ({ row }) => (
        <Box xcss={titleStyles}>
          <RouterLink to={ROUTES.RELEASE_NOTE_DETAILS(row.original.id)}>
            <Text color="color.link" weight="regular">
              {row.original.title}
            </Text>
          </RouterLink>
        </Box>
      ),
    },
    {
      id: 'changeType',
      header: formatMessage(messages.changeTypeHeader),
      accessorKey: 'type',
      size: 130,
      enableSorting: false,
      cell: ({ row }) => <UpdateChangeType type={row.original.type} />,
    },
    {
      id: 'previewTrackReleaseDate',
      header: formatMessage(messages.previewTrackHeader),
      accessorKey: 'previewTrackReleaseDate',
      size: 160,
      enableSorting: false,
      cell: ({ row }) => (
        <Text>
          {row.original.previewTrackReleaseDate
            ? formatBundleDate(row.original.previewTrackReleaseDate)
            : '—'}
        </Text>
      ),
    },
    {
      id: 'bundledTrackReleaseDate',
      header: formatMessage(messages.bundledTrackHeader),
      accessorKey: 'bundledTrackReleaseDate',
      size: 160,
      enableSorting: false,
      cell: ({ row }) => (
        <Text>
          {row.original.bundledTrackReleaseDate
            ? formatBundleDate(row.original.bundledTrackReleaseDate)
            : '—'}
        </Text>
      ),
    },
  ];

  const tableState = state.bundles.error
    ? TableState.error
    : state.bundles.isLoading
    ? TableState.loading
    : TableState.loaded;

  return (
    <Stack space="space.200">
      <CharlieTable
        key={
          !analyticsOptOut
            ? 'unread-indicator-visible'
            : 'unread-indicator-hidden'
        }
        data={state.bundles.data}
        columnDefs={columnDefs}
        manualSorting={false}
        tableState={tableState}
        emptyTableView={
          <EmptyState
            header={formatMessage(messages.emptyStateHeader)}
            description={formatMessage(messages.emptyStateDescription)}
          />
        }
        errorView={
          <EmptyState
            header={formatMessage(messages.errorStateHeader)}
            description={formatMessage(messages.errorStateDescription)}
          />
        }
        tableType="INVENTORY"
        manualPagination={true}
      />
      <Pagination
        hasPreviousPage={!!state.bundles?.links?.prev}
        hasNextPage={!!state.bundles?.links?.next}
        onNextPageClicked={onNextPageClicked}
        onPreviousPageClicked={onPreviousPageClicked}
        onPageSizeChange={onPageSizeChange}
      />
    </Stack>
  );
};
